import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import ButtonText from '@segment/matcha/components/ButtonText'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { SectionWithAccordion } from 'views/Infrastructure/accordionData'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import AccordionSection from '../AccordionSection'

import AggregationIdentityResolutionImage from './AggregationIdentityResolutionImage'

const data = {
  anchorId: `identity-resolution`,
  overline: `Aggregation`,
  heading: `Identity Resolution`,
  description: `Emails, device IDs, primary keys in your database, account IDs—a single user might be keyed in hundreds of different ways! You can’t just overwrite those ties or endlessly traverse cycles in your identity graph. You need a bullet-proof system for tying those users together. Here’s how we do it.`,
  buttonTextLink: `https://segment.com/blog/identity-resolution/`,
  buttonTextLinkText: `Read our blog`,
  accordionSection: SectionWithAccordion.IDENTITY_RESOLUTION,
}

const AggregationIdentityResolution: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  const { anchorId, overline, heading, description, buttonTextLink, buttonTextLinkText, accordionSection } = data
  return (
    <Wrapper id={anchorId}>
      <SectionBase variant="2Col" copyVariant="wide">
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Overline>{overline}</CopyText.Overline>
            <CopyText.Heading>{heading}</CopyText.Heading>
            <CopyText.Description tag="div">
              <Spacer>{description}</Spacer>
            </CopyText.Description>
            <Spacer>
              <ButtonText tag="a" href={getAbsoluteUrl(buttonTextLink, pageConfig)} iconEnd={ChevronRightIcon}>
                {buttonTextLinkText}
              </ButtonText>
            </Spacer>
            <AccordionSection section={accordionSection} />
          </CopyTextBase>
        </Section.Copy>
        <StyledSectionContents>
          <AggregationIdentityResolutionImage />
        </StyledSectionContents>
      </SectionBase>
    </Wrapper>
  )
}

const Spacer = styled.div`
  margin-bottom: 32px;
`

const Wrapper = styled.section`
  display: grid;
`

const StyledSectionContents = styled(Section.Contents)`
  margin: auto;
`

export default AggregationIdentityResolution
